/* Tikken op de strip schuift naar de volgende USP, en na de laatste weer naar
   de eerste. Het swipen zelf doet de CSS met scroll-snap; dit voegt alleen de
   tik toe voor wie niet sleept.

   Welk element de scrollende baan is en welke kinderen de panelen zijn komt
   uit data-attributen, zodat zowel de topbar (waar het custom element zelf de
   baan is) als de FAQ-afbeelding (waar de baan een kind is) hetzelfde gedrag
   delen. */
class UspCarousel extends HTMLElement {
  /* Vanaf deze breedte staan de USP's naast elkaar en valt er niets te
     schuiven. Per plek te overrulen met data-usp-swipe-below. */
  static DEFAULT_BREAKPOINT = 900;

  /* Opt-in per plek met data-usp-autoplay (optioneel met een eigen
     interval in ms). Alle USP-carousels in het thema hebben het aan. */
  static DEFAULT_AUTOPLAY_DELAY = 4000;

  connectedCallback() {
    this.track = this.querySelector('[data-usp-track]') || this;
    this.items = Array.from(this.track.querySelectorAll('[data-usp-item]'));

    if (this.items.length < 2) return;

    this.breakpoint =
      Number(this.dataset.uspSwipeBelow) || UspCarousel.DEFAULT_BREAKPOINT;

    /* data-usp-swipe-on="overflow" schakelt niet op een vaste breedte om, maar
       zodra de USP's samen niet meer naast elkaar passen. */
    this.swipeOnOverflow = this.dataset.uspSwipeOn === 'overflow';
    if (this.swipeOnOverflow) this.watchFit();

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);

    if ('uspAutoplay' in this.dataset) {
      this.autoplayDelay =
        Number(this.dataset.uspAutoplay) ||
        UspCarousel.DEFAULT_AUTOPLAY_DELAY;
      this.startAutoplay();
      this.pauseAutoplay = this.pauseAutoplay.bind(this);
      this.track.addEventListener('pointerdown', this.pauseAutoplay);
      this.track.addEventListener('touchstart', this.pauseAutoplay, {
        passive: true,
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.stopAutoplay();
    this.track.removeEventListener('pointerdown', this.pauseAutoplay);
    this.track.removeEventListener('touchstart', this.pauseAutoplay);
  }

  /* De ouder meten en niet het element zelf: het element krimpt en groeit juist
     door het omschakelen, wat de observer eindeloos zou laten rondgaan. */
  watchFit() {
    this.measureFit = this.measureFit.bind(this);
    this.measureFit();
    this.resizeObserver = new ResizeObserver(this.measureFit);
    this.resizeObserver.observe(this.parentElement || this);
    if (document.fonts) document.fonts.ready.then(this.measureFit);
  }

  /* Meten kan alleen in de rij-stand: pas dan verraadt de scrollbreedte of de
     USP's samen breder zijn dan de ruimte die er is. */
  measureFit() {
    this.classList.remove('is-carousel');
    const overflows = this.track.scrollWidth > this.track.clientWidth + 1;

    /* De breedste USP in de rij-stand, zodat de CSS de carousel precies zo
       breed kan maken als zijn inhoud in plaats van de volle ruimte. */
    const widest = Math.max(
      ...this.items.map((item) => item.getBoundingClientRect().width)
    );
    this.style.setProperty('--usp-item-max-width', `${Math.ceil(widest)}px`);

    this.classList.toggle('is-carousel', overflows);
  }

  isSwipeable() {
    if (this.swipeOnOverflow) return this.classList.contains('is-carousel');
    return !window.matchMedia(`(min-width: ${this.breakpoint}px)`).matches;
  }

  startAutoplay() {
    this.autoplayTimer = setInterval(() => {
      if (this.isSwipeable()) this.advance();
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    clearInterval(this.autoplayTimer);
  }

  /* Zodra iemand zelf sleept, stopt het automatisch doorschuiven - anders
     vecht de timer met de eigen swipe van de bezoeker. */
  pauseAutoplay() {
    this.stopAutoplay();
  }

  onClick() {
    if (!this.isSwipeable()) return;
    this.advance();
  }

  advance() {
    /* De -1 vangt de afronding op die een halve pixel scrollpositie kan
       opleveren, anders blijft hij op het huidige paneel hangen. */
    const currentIndex = this.items.findIndex(
      (item) => item.offsetLeft >= this.track.scrollLeft - 1
    );
    const nextIndex = (Math.max(currentIndex, 0) + 1) % this.items.length;

    /* Terug naar het begin altijd naar 0, zodat een eventuele padding aan de
       linkerkant van de baan weer zichtbaar wordt. */
    this.track.scrollTo({
      left: nextIndex === 0 ? 0 : this.items[nextIndex].offsetLeft,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('usp-carousel')) {
  customElements.define('usp-carousel', UspCarousel);
}
